import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { ERROR_CODES } from '@fkwd-qc/shared';
import { ApiRequestError } from '@/lib/api';

/**
 * Props for the page error boundary.
 *
 * @since 1.0.0
 */
export interface PageErrorBoundaryProps {
    children: ReactNode;
    onRetry?: () => void;
    resetKey?: string;
}

/**
 * State for the page error boundary.
 *
 * @since 1.0.0
 */
export interface PageErrorBoundaryState {
    error: Error | null;
    resetKey?: string;
}

/**
 * What the failure panel says when the error is not one the API described.
 *
 * @since 1.3.0
 */
export const UNEXPECTED_FAILURE_MESSAGE =
    'Something went wrong while drawing this view. Try again, and quote the reference below if it keeps happening.';

/**
 * Picks the sentence shown under the heading: the worker's own wording for an API failure,
 * a fixed line for anything else so no internal detail reaches the screen.
 *
 * @since 1.3.0
 * @param error - the caught error
 * @returns the message to display
 */
function failureMessage(error: Error): string {
    return error instanceof ApiRequestError ? error.message : UNEXPECTED_FAILURE_MESSAGE;
}

/**
 * Builds the reference line shown under a failure so a user can quote it in a report.
 *
 * @since 1.0.0
 * @param error - the caught error
 * @returns the reference line
 */
function referenceLine(error: Error): string {
    if (!(error instanceof ApiRequestError)) {
        return `Reference ${ERROR_CODES.internal}`;
    }

    if (error.requestId === '') {
        return `Reference ${error.code}`;
    }

    return `Reference ${error.code} · request ${error.requestId}`;
}

/**
 * Catches render and data failures inside one page and offers a retry.
 *
 * @since 1.0.0
 */
export class PageErrorBoundary extends Component<PageErrorBoundaryProps, PageErrorBoundaryState> {
    /**
     * Sets up the boundary in its healthy state.
     *
     * @since 1.0.0
     * @param props - the subtree to guard and the optional retry hook
     */
    constructor(props: PageErrorBoundaryProps) {
        super(props);
        this.state = { error: null, resetKey: props.resetKey };
    }

    /**
     * Records a caught error so the next render shows the failure panel.
     *
     * @since 1.0.0
     * @param error - the error thrown below the boundary
     * @returns the next state
     */
    static getDerivedStateFromError(error: Error): Partial<PageErrorBoundaryState> {
        return { error };
    }

    /**
     * Clears the failure when the guarded route changes, so navigating away recovers.
     *
     * @since 1.0.0
     * @param props - the incoming props
     * @param state - the current state
     * @returns the next state, or null when nothing changes
     */
    static getDerivedStateFromProps(
        props: PageErrorBoundaryProps,
        state: PageErrorBoundaryState,
    ): Partial<PageErrorBoundaryState> | null {
        if (props.resetKey === state.resetKey) {
            return null;
        }

        return { error: null, resetKey: props.resetKey };
    }

    /**
     * Logs the failure for whoever is watching the console.
     *
     * @since 1.0.0
     * @param error - the error thrown below the boundary
     * @param info - React's component stack for the failure
     * @returns void
     */
    override componentDidCatch(error: Error, info: ErrorInfo): void {
        console.error('[fkwd-qc] page failed to render', error, info.componentStack);
    }

    /**
     * Clears the failure and asks the caller to refetch.
     *
     * @since 1.0.0
     * @returns void
     */
    handleRetry = (): void => {
        this.setState({ error: null });
        this.props.onRetry?.();
    };

    /**
     * Renders the guarded subtree, or the failure panel when something threw.
     *
     * @since 1.0.0
     * @returns the rendered subtree
     */
    override render(): ReactNode {
        const { error } = this.state;

        if (error === null) {
            return this.props.children;
        }

        return (
            <div className="fkwd-panel" role="alert">
                <h2>This view could not load</h2>
                <p>{failureMessage(error)}</p>
                <span className="fkwd-panel__code">{referenceLine(error)}</span>
                <button className="fkwd-btn fkwd-btn--dark" onClick={this.handleRetry} type="button">
                    Try again
                </button>
            </div>
        );
    }
}
